import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Zap, Loader2, Crown, Moon, Sun, Home as HomeIcon, History as HistoryIcon, CreditCard, Menu, User, LogOut, Clock, X, TrendingUp, Shield, Sparkles } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import AddToHomeScreen from '@/components/AddToHomeScreen';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Pricing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, logout, refreshUser } = useAuth();
  const [packages, setPackages] = useState({ topups: {}, subscriptions: {} });
  const [loading, setLoading] = useState({});
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const storedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(storedDarkMode);
    if (storedDarkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }

    const handleStorageChange = (e) => {
      if (e.key === 'darkMode') {
        const newDarkMode = e.newValue === 'true';
        setDarkMode(newDarkMode);
        if (newDarkMode) {
          document.body.classList.add('dark');
        } else {
          document.body.classList.remove('dark');
        }
      }
    };

    const handleDarkModeChange = (e) => {
      const newDarkMode = e.detail;
      setDarkMode(newDarkMode);
      if (newDarkMode) {
        document.body.classList.add('dark');
      } else {
        document.body.classList.remove('dark');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('darkModeChange', handleDarkModeChange);

    loadPackages();
    if (isAuthenticated) {
      loadSubscription();
    }
    checkPaymentReturn();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('darkModeChange', handleDarkModeChange);
    };
  }, [isAuthenticated]);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', String(newDarkMode));
    if (newDarkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
    window.dispatchEvent(new CustomEvent('darkModeChange', { detail: newDarkMode }));
  };

  const loadPackages = async () => {
    try {
      const response = await axios.get(`${API}/payment/packages`);
      if (response.data.success) {
        setPackages(response.data.packages);
      }
    } catch (error) {
      console.error('Failed to load packages:', error);
      toast.error('Failed to load pricing packages');
    }
  };

  const loadSubscription = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/subscription/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.subscription) {
        setSubscription(response.data.subscription);
      }
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
  };

  const handlePurchase = async (packageId, paymentType) => {
    if (!isAuthenticated) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }

    console.log('🛒 Purchase initiated:', { packageId, paymentType, isAuthenticated });
    setLoading({ ...loading, [packageId]: true });

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
        return;
      }
      
      console.log('📦 Creating checkout for package:', packageId);
      console.log('💳 Payment type:', paymentType);
      
      const payload = {
        package_id: packageId,
        payment_type: paymentType,
        origin_url: window.location.origin
      };
      
      console.log('📤 Request payload:', payload);
      
      const response = await axios.post(
        `${API}/payment/checkout`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000 // 10 second timeout
        }
      );

      console.log('✅ Checkout response:', response.data);

      if (response.data.success && response.data.url) {
        console.log('🔗 Redirecting to Stripe:', response.data.url);
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        throw new Error(response.data.error || 'No checkout URL received');
      }
    } catch (error) {
      console.error('❌ Purchase error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        packageId,
        paymentType
      });
      
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to initiate payment';
      toast.error(`Payment error: ${errorMessage}`);
      setLoading({ ...loading, [packageId]: false });
    }
  };

  const checkPaymentReturn = async () => {
    const sessionId = searchParams.get('session_id');
    const paymentType = searchParams.get('type');
    
    // Always reset loading states when returning to pricing page
    setLoading({});
    
    if (sessionId) {
      setCheckingPayment(true);
      toast.info('Checking payment status...');
      await pollPaymentStatus(sessionId, paymentType);
    }
  };

  const pollPaymentStatus = async (sessionId, paymentType, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      setLoading({}); // Reset all loading states
      toast.error('Payment verification timed out. Please refresh to check your credits.');
      return;
    }

    try {
      // Use the old working endpoint
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);

      if (response.data.payment_status === 'paid') {
        setCheckingPayment(false);
        setLoading({}); // Reset all loading states
        
        // Refresh user data
        await refreshUser();
        
        toast.success('Payment successful! Credits added to your account.');
        
        // Clear URL params
        window.history.replaceState({}, '', '/pricing');
        return;
      } else if (response.data.status === 'expired') {
        setCheckingPayment(false);
        setLoading({}); // Reset all loading states
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, '', '/pricing');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, paymentType, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
      toast.error('Error verifying payment. Please contact support.');
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your billing period.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API}/subscription/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Subscription cancelled successfully');
        await loadSubscription();
      } else {
        toast.error(response.data.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-b from-slate-50 to-blue-50'}`}>
      {/* Header */}
      <header className={`border-b ${darkMode ? 'border-gray-700 bg-gray-800/80' : 'border-blue-100 bg-white/80'} backdrop-blur-sm sticky top-0 z-50`}>
        <div className="max-w-6xl mx-auto px-4 py-1 md:py-1.5 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/')}>
            <img 
              src={darkMode 
                ? "https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/p4o00bk8_Emo.zip%20-%205.png"
                : "https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/3r5buf5n_Emo.zip%20-%204.png"
              }
              alt="EmoQ - Go to Home"
              title="Go to Home"
              className="h-12 md:h-14 w-auto transition-all"
            />
          </div>
          
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={toggleDarkMode} className="gap-2">
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <AddToHomeScreen darkMode={darkMode} />
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="gap-2">
              <HomeIcon className="w-4 h-4" />
              Home
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/history')} className="gap-2">
              <HistoryIcon className="w-4 h-4" />
              My Analyses
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/pricing')} className="gap-2">
              <CreditCard className="w-4 h-4" />
              Pricing
            </Button>
            
            {isAuthenticated && user && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                <Zap className="w-4 h-4 text-blue-600" />
                <span className={`text-sm font-bold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  {user.credits || 0} credits
                </span>
              </div>
            )}
            
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={user?.picture} alt={user?.name || user?.email} />
                      <AvatarFallback>
                        {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden lg:inline">{user?.name || user?.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>
                    <div className="text-sm">
                      <div className="font-semibold">{user?.name || 'User'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">⚡ {user?.credits || 0} credits</span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="w-4 h-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/pricing')}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Top Up Credits
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="gap-2">
                <User className="w-4 h-4" />
                Login
              </Button>
            )}
          </div>

          {/* Mobile menu */}
          <div className="flex md:hidden items-center gap-2">
            {isAuthenticated && user && (
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                <Zap className="w-3 h-3 text-blue-600" />
                <span className="font-bold">{user.credits || 0}</span>
              </div>
            )}
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <Menu className={`w-6 h-6 ${darkMode ? 'text-white' : 'text-gray-900'}`} />
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className={`md:hidden border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-blue-100 bg-white'}`}>
            <div className="px-4 py-2 space-y-2">
              <Button variant="ghost" size="sm" onClick={toggleDarkMode} className="w-full justify-start gap-2">
                {darkMode ? <><Sun className="w-4 h-4" />Light Mode</> : <><Moon className="w-4 h-4" />Dark Mode</>}
              </Button>
              <AddToHomeScreen darkMode={darkMode} />
              <Button variant="ghost" size="sm" onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                <HomeIcon className="w-4 h-4" />
                Home
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                <HistoryIcon className="w-4 h-4" />
                My Analyses
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                <CreditCard className="w-4 h-4" />
                Pricing
              </Button>
              
              {isAuthenticated ? (
                <>
                  <div className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 border-t dark:border-gray-700">
                    <div className="font-semibold">{user?.name || user?.email}</div>
                    <div className="text-xs text-gray-500">Credits: {user?.credits || 0}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                    <User className="w-4 h-4" />
                    My Profile
                  </Button>
                  <Button variant="ghost" size="sm" onClick={logout} className="w-full justify-start gap-2">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                  <User className="w-4 h-4" />
                  Login
                </Button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Choose Your Plan
          </h1>
          <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Get started with credits or subscribe for the best value
          </p>
        </div>

        {/* Current Subscription Status */}
        {subscription && subscription.status === 'active' && (
          <Card className={`mb-8 ${darkMode ? 'bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-blue-700' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Crown className="w-6 h-6 text-yellow-500" />
                    Active Subscription
                  </CardTitle>
                  <CardDescription className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                    You're on the <strong>{packages.subscriptions[subscription.package_id]?.name}</strong>
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelSubscription}
                  className="text-red-600 hover:text-red-700"
                >
                  Cancel
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Credits per month:</span>
                  <span className={`ml-2 font-bold ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                    {subscription.credits_per_month}
                  </span>
                </div>
                <div>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Next billing:</span>
                  <span className={`ml-2 font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatDate(subscription.next_billing_date)}
                  </span>
                </div>
                <div>
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Amount:</span>
                  <span className={`ml-2 font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    ${subscription.amount}/month
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credit Top-Up Packages */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h2 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Credit Packages
            </h2>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              One-time purchases with bonus credits - use anytime, never expire
            </p>
          </div>
          
          {Object.keys(packages.topups).length === 0 ? (
            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
              <p>Loading pricing packages...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Object.entries(packages.topups).map(([key, pkg]) => {
                // Calculate savings
                const baseRate = 0.10; // $0.10 per credit (100 credits = $10)
                const actualRate = pkg.amount / pkg.credits;
                const savingsPercent = Math.round(((baseRate - actualRate) / baseRate) * 100);
                
                return (
                  <Card 
                    key={key} 
                    className={`${
                      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    } hover:shadow-xl transition-all hover:scale-105 ${
                      pkg.bonus && pkg.bonus >= 100 ? 'ring-2 ring-purple-500' : ''
                    }`}
                  >
                    <CardHeader>
                      <CardTitle className={`${darkMode ? 'text-white' : 'text-gray-900'} text-center`}>
                        <div className="text-4xl font-bold mb-2">
                          {pkg.credits.toLocaleString()}
                        </div>
                        <div className="text-sm font-normal text-gray-500">Credits</div>
                        {pkg.bonus && (
                          <Badge className="mt-2 bg-green-600 text-white">
                            +{pkg.bonus} BONUS
                          </Badge>
                        )}
                        {savingsPercent > 0 && (
                          <Badge className="mt-1 bg-blue-600 text-white">
                            Save {savingsPercent}%
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="text-center">
                        <div className="text-3xl font-bold text-blue-600">${pkg.amount}</div>
                        <div className="text-xs mt-1">${(pkg.amount / pkg.credits).toFixed(3)} per credit</div>
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={() => handlePurchase(key, 'topup')}
                        disabled={loading[key] || checkingPayment}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                      >
                        {loading[key] ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                        ) : (
                          'Buy Now'
                        )}
                      </Button>
                      {pkg.bonus && (
                        <p className={`text-xs text-center mt-3 ${darkMode ? 'text-green-400' : 'text-green-600'} font-semibold`}>
                          Total: {(pkg.credits + pkg.bonus).toLocaleString()} credits
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Section 3: Enterprise (Coming Soon) */}
        <div className="mb-8">
          <div className="text-center mb-8">
            <h2 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Enterprise
            </h2>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Custom solutions for large teams and organizations
            </p>
          </div>
          
          <Card className={`${darkMode ? 'bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border-purple-700' : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200'}`}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <Shield className="w-6 h-6 text-purple-600" />
                Enterprise API Access
                <Badge variant="outline" className="ml-2">Coming Soon</Badge>
              </CardTitle>
              <CardDescription className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                Powerful features for businesses and developers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Custom Credit Limits</h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Tailored packages for your needs</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>API Access & Keys</h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Integrate EmoQ into your apps</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Webhook Integrations</h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Real-time analysis updates</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Priority Processing</h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Faster analysis results</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                    <User className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Dedicated Support</h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Direct line to our team</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                    <CreditCard className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Custom Pricing</h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Volume discounts available</p>
                  </div>
                </div>
              </div>
              
              <Button disabled className="w-full md:w-auto">
                <Clock className="w-4 h-4 mr-2" />
                Coming Soon
              </Button>
              <p className={`text-xs mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Enterprise features will be available soon. Stay tuned for updates!
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Info Section */}
        <div className={`text-center mt-12 p-6 rounded-lg ${darkMode ? 'bg-gray-800/50' : 'bg-blue-50'}`}>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Need Help Choosing?
          </h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Subscribers get {' '}
            <strong>20-30% more value</strong> than top-ups, plus exclusive benefits!
            <br />
            You can always top-up additional credits if you run out during your subscription.
          </p>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            All plans include access to all features. Subscriptions renew monthly and can be cancelled anytime.
          </p>
        </div>
      </div>

      {/* Loading Overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <Card className={darkMode ? 'bg-gray-800' : 'bg-white'}>
            <CardContent className="p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-600" />
              <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Verifying Payment...
              </p>
              <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Please wait while we confirm your payment
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <Footer darkMode={darkMode} />
    </div>
  );
};

export default Pricing;
